import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from "react";
import { createPortal } from "react-dom";

import {
  getWidgetConfigFields,
  type WidgetConfigField,
  type WidgetConfigOption,
  type WidgetInstanceConfig,
} from "src/charts/widgetConfig";
import { getWidgetTabOptions } from "src/components/builder/widgetTabOptions";

import "./WidgetConfigMenu.scss";

const MENU_WIDTH = 210;
const SUBMENU_WIDTH = 170;

type Props = {
  chartType: string;
  /** Current saved/pending config for this widget instance. */
  config: WidgetInstanceConfig;
  /** Receives the complete next config — callers persist it wholesale. */
  onChange: (next: WidgetInstanceConfig) => void;
  /** The widget's payload, used to list real tabs for "Set Default Tab". */
  widgetData: unknown;
  /** Widget title, for the button's accessible name. */
  widgetLabel: string;
};

/**
 * The builder's per-widget settings menu — the gear beside the remove (X)
 * button on a canvas card.
 *
 * Its contents are not hardcoded: it renders exactly the fields
 * charts/widgetConfig.ts declares for this chart type, so a chart with no
 * settings never gets a gear (Slot checks hasWidgetConfigFields first).
 *
 * Built as a portalled fixed-position menu rather than a Bootstrap dropdown
 * because Bootstrap 5 has no submenu support and "Set Default Tab" needs one.
 * Follows the KpiExportMenu pattern (same open/close mechanics and styling
 * vocabulary) so the two menus behave identically.
 */
export default function WidgetConfigMenu({
  chartType,
  config,
  onChange,
  widgetData,
  widgetLabel,
}: Props): JSX.Element | null {
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  // Submenus open to the right by default and flip left when the card sits
  // near the viewport edge, which is common for the right-hand grid columns.
  const [flipSubmenu, setFlipSubmenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setMenuPos(null);
    setOpenSubmenu(null);
  }, []);

  // Outside click / Esc close — same contract as KpiExportMenu.
  useEffect(() => {
    if (!menuPos) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuPos, close]);

  const fields = getWidgetConfigFields(chartType);
  if (fields.length === 0) return null;

  const openMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Right-align the menu under the gear, clamped into the viewport.
    const x = Math.min(
      Math.max(rect.right - MENU_WIDTH, 8),
      Math.max(window.innerWidth - MENU_WIDTH - 8, 8),
    );
    setFlipSubmenu(x + MENU_WIDTH + SUBMENU_WIDTH + 8 > window.innerWidth);
    setMenuPos({ x, y: rect.bottom + 4 });
  };

  const setValue = (key: keyof WidgetInstanceConfig, value: unknown) => {
    const next: WidgetInstanceConfig = { ...config };
    if (value === undefined) {
      delete next[key];
    } else if (key === "hide_line_chart") {
      next.hide_line_chart = value as boolean;
    } else if (key === "default_tab") {
      next.default_tab = value as string;
    } else if (key === "chart_type") {
      next.chart_type = value as string;
    }
    onChange(next);
  };

  const optionsFor = (field: WidgetConfigField): WidgetConfigOption[] =>
    field.control === "tabs"
      ? getWidgetTabOptions(chartType, widgetData)
      : (field.options?.(chartType) ?? []);

  const renderToggle = (field: WidgetConfigField) => {
    // Reads through field.key so a second toggle can be added to
    // widgetConfig.ts without this silently reporting the wrong one's state.
    const isOn = config[field.key] === true;
    return (
      <button
        key={field.key}
        type="button"
        className="widget-config__item"
        role="menuitemcheckbox"
        aria-checked={isOn}
        onMouseEnter={() => setOpenSubmenu(null)}
        onClick={() => {
          // Off clears the key rather than storing `false`: the two render
          // identically, and an absent key keeps the saved config minimal.
          setValue(field.key, isOn ? undefined : true);
          close();
        }}
      >
        <i
          className={`bi ${isOn ? "bi-check-square" : "bi-square"}`}
          aria-hidden="true"
        />
        <span className="widget-config__label">{field.label}</span>
      </button>
    );
  };

  const renderSubmenuField = (field: WidgetConfigField) => {
    const options = optionsFor(field);
    // Nothing to choose from (e.g. a flat, untabbed payload) — offering an
    // empty submenu would be a dead end, so drop the row entirely.
    if (options.length === 0) return null;

    const current = config[field.key];
    const activeOption = options.find((option) => option.value === current);
    const isOpen = openSubmenu === field.key;

    return (
      <div
        key={field.key}
        className="widget-config__row"
        onMouseEnter={() => setOpenSubmenu(field.key)}
      >
        <button
          type="button"
          className="widget-config__item widget-config__item--parent"
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => setOpenSubmenu(isOpen ? null : field.key)}
        >
          <i className="bi bi-sliders2" aria-hidden="true" />
          <span className="widget-config__label">{field.label}</span>
          {activeOption && (
            <span className="widget-config__value">{activeOption.label}</span>
          )}
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>

        {isOpen && (
          <div
            className={`widget-config__submenu${
              flipSubmenu ? " widget-config__submenu--flip" : ""
            }`}
            role="menu"
            style={{ width: SUBMENU_WIDTH }}
          >
            {/* Clearing has to be reachable, or a setting could never be
                undone once picked. */}
            <button
              type="button"
              className="widget-config__item"
              role="menuitemradio"
              aria-checked={current === undefined}
              onClick={() => {
                setValue(field.key, undefined);
                close();
              }}
            >
              <i
                className={`bi ${current === undefined ? "bi-check2" : "bi-dash"}`}
                aria-hidden="true"
              />
              <span className="widget-config__label">Default</span>
            </button>
            <div className="widget-config__divider" />
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="widget-config__item"
                role="menuitemradio"
                aria-checked={current === option.value}
                onClick={() => {
                  setValue(field.key, option.value);
                  close();
                }}
              >
                <i
                  className={`bi ${
                    current === option.value ? "bi-check2" : "bi-dash"
                  }`}
                  aria-hidden="true"
                />
                <span className="widget-config__label">{option.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="builder-canvas-card__action"
        aria-label={`Settings for ${widgetLabel}`}
        aria-haspopup="menu"
        aria-expanded={menuPos !== null}
        onClick={() => (menuPos ? close() : openMenu())}
      >
        <i className="bi bi-gear" aria-hidden="true" />
      </button>

      {menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="widget-config__menu"
            role="menu"
            style={{ left: menuPos.x, top: menuPos.y, width: MENU_WIDTH }}
            onMouseLeave={() => setOpenSubmenu(null)}
          >
            {fields.map((field) =>
              field.control === "toggle"
                ? renderToggle(field)
                : renderSubmenuField(field),
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
